import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet'
import MarkerClusterGroup from 'react-leaflet-cluster'
import { Icon, divIcon } from 'leaflet'
import { useEffect, useRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useView } from '../contexts/ViewContext'
import { useData } from '../contexts/DataContext'
import { useFilters } from '../contexts/FilterContext'
import { useLocation } from '../contexts/LocationContext'
import { useFilteredFacilities } from '../hooks/useFilteredFacilities'
import LoadingSpinner from '../components/common/LoadingSpinner'
import InsightsPanel from '../components/InsightsPanel'
import { countKeyServices } from '../utils/facilityUtils'
import 'leaflet/dist/leaflet.css'

// Custom marker styles
const markerStyles = `
  .custom-marker {
    background: none;
    border: none;
  }

  .marker-pin {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  }

  .marker-icon {
    transform: rotate(45deg);
    font-size: 12px;
    color: white;
  }

  .hc2-pin {
    background-color: #10b981;
    width: 20px;
    height: 20px;
  }

  .hc3-pin {
    background-color: #3b82f6;
    width: 24px;
    height: 24px;
  }

  .hc4-pin {
    background-color: #f59e0b;
    width: 28px;
    height: 28px;
  }

  .hospital-pin {
    background-color: #ef4444;
    width: 32px;
    height: 32px;
  }

  .clinic-pin {
    background-color: #8b5cf6;
    width: 18px;
    height: 18px;
  }

  .primary-school-pin {
    background-color: #3b82f6;
    width: 22px;
    height: 22px;
  }

  .secondary-school-pin {
    background-color: #10b981;
    width: 26px;
    height: 26px;
  }

  .cluster-marker {
    background-color: #3b82f6;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 35px;
    height: 35px;
    border: 2px solid white;
    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  }

  .cluster-count {
    color: white;
    font-weight: bold;
    font-size: 14px;
  }

  .custom-cluster-icon {
    background: none;
    border: none;
  }

  .district-marker {
    background-color: #1f2937;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid white;
    box-shadow: 0 4px 8px rgba(0,0,0,0.4);
  }

  .district-marker-text {
    color: white;
    font-weight: bold;
    font-size: 12px;
    text-align: center;
    line-height: 1;
  }
`

// Inject styles
if (!document.getElementById('map-marker-styles')) {
  const styleSheet = document.createElement('style')
  styleSheet.id = 'map-marker-styles'
  styleSheet.textContent = markerStyles
  document.head.appendChild(styleSheet)
}

// Fix for default markers in React-Leaflet
delete Icon.Default.prototype._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

// Custom icons for different facility levels with different colors
const facilityIcons = {
  HCII: new divIcon({
    className: 'custom-marker hc2-marker',
    html: `<div class="marker-pin hc2-pin">
             <div class="marker-icon"></div>
           </div>`,
    iconSize: [24, 32],
    iconAnchor: [12, 32],
    popupAnchor: [0, -32],
  }),
  HCIII: new divIcon({
    className: 'custom-marker hc3-marker',
    html: `<div class="marker-pin hc3-pin">
             <div class="marker-icon"></div>
           </div>`,
    iconSize: [28, 36],
    iconAnchor: [14, 36],
    popupAnchor: [0, -36],
  }),
  HCIV: new divIcon({
    className: 'custom-marker hc4-marker',
    html: `<div class="marker-pin hc4-pin">
             <div class="marker-icon"></div>
           </div>`,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -40],
  }),
  Hospital: new divIcon({
    className: 'custom-marker hospital-marker',
    html: `<div class="marker-pin hospital-pin">
             <div class="marker-icon"></div>
           </div>`,
    iconSize: [36, 44],
    iconAnchor: [18, 44],
    popupAnchor: [0, -44],
  }),
  Clinic: new divIcon({
    className: 'custom-marker clinic-marker',
    html: `<div class="marker-pin clinic-pin">
             <div class="marker-icon"></div>
           </div>`,
    iconSize: [20, 28],
    iconAnchor: [10, 28],
    popupAnchor: [0, -28],
  }),
  Primary: new divIcon({
    className: 'custom-marker primary-school-marker',
    html: `<div class="marker-pin primary-school-pin">
             <div class="marker-icon">🏫</div>
           </div>`,
    iconSize: [26, 32],
    iconAnchor: [13, 32],
    popupAnchor: [0, -32],
  }),
  Secondary: new divIcon({
    className: 'custom-marker secondary-school-marker',
    html: `<div class="marker-pin secondary-school-pin">
             <div class="marker-icon">🎓</div>
           </div>`,
    iconSize: [30, 36],
    iconAnchor: [15, 36],
    popupAnchor: [0, -36],
  }),
}

function FacilityMarker({ facility, onViewDetails, category }) {
  // Determine icon based on category and facility type
  let icon
  if (category === 'education') {
    const institutionType = facility.institution_type || 'Primary'
    icon = facilityIcons[institutionType] || facilityIcons['Primary']
  } else {
    // Health facilities
    const facilityLevel = facility.level || 'HCII'
    icon = facilityIcons[facilityLevel] || facilityIcons['HCII']
  }

  // Get name based on category
  const facilityName =
    category === 'education'
      ? facility.school_name || facility.name
      : facility.name

  return (
    <Marker position={[facility.latitude, facility.longitude]} icon={icon}>
      <Popup>
        <div className="min-w-48">
          <h3 className="font-medium text-gray-900 mb-1 capitalize">
            {facilityName}
          </h3>
          <div className="space-y-1 text-sm">
            {category === 'education' ? (
              // Education facility popup content
              <>
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">Type:</span>
                  <span className="ml-1 text-gray-600">
                    {facility.institution_type}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">Ownership:</span>
                  <span className="ml-1 text-gray-600">
                    {facility.ownership}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">Location:</span>
                  <span className="ml-1 text-gray-600">
                    {facility.subcounty}, {facility.district}
                  </span>
                </div>
                {facility.total_learners && (
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700">Students:</span>
                    <span className="ml-1 text-gray-600">
                      {facility.total_learners}
                    </span>
                  </div>
                )}
                {facility.total_teachers && (
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700">Teachers:</span>
                    <span className="ml-1 text-gray-600">
                      {facility.total_teachers}
                    </span>
                  </div>
                )}
              </>
            ) : (
              // Health facility popup content (existing)
              <>
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">Level:</span>
                  <span className="ml-1 text-gray-600">{facility.level}</span>
                </div>
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">Location:</span>
                  <span className="ml-1 text-gray-600">
                    {facility.parish}, {facility.district}
                  </span>
                </div>
                <div className="flex items-center">
                  <span className="font-medium text-gray-700">
                    Key Services:
                  </span>
                  <span className="ml-1 text-gray-600">
                    {countKeyServices(facility)} available
                  </span>
                </div>
                {facility.officer_in_charge && (
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700">Officer:</span>
                    <span className="ml-1 text-gray-600 capitalize">
                      {facility.officer_in_charge.toLowerCase()}
                    </span>
                  </div>
                )}
              </>
            )}
          </div>
          <button
            onClick={() => onViewDetails?.(facility)}
            className="mt-2 w-full bg-primary-500 text-white text-sm py-1 px-2 rounded hover:bg-primary-600 transition-colors"
          >
            View Details
          </button>
        </div>
      </Popup>
    </Marker>
  )
}

FacilityMarker.propTypes = {
  facility: PropTypes.shape({
    name: PropTypes.string,
    school_name: PropTypes.string,
    level: PropTypes.string,
    institution_type: PropTypes.string,
    ownership: PropTypes.string,
    latitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    longitude: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    parish: PropTypes.string,
    subcounty: PropTypes.string,
    district: PropTypes.string,
    officer_in_charge: PropTypes.string,
    total_learners: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    total_teachers: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  onViewDetails: PropTypes.func.isRequired,
  category: PropTypes.string.isRequired,
}

function DistrictMarker({ district, facilityCount, onDistrictClick }) {
  // Calculate marker size based on facility count
  const getMarkerSize = count => {
    if (count < 10) return 40
    if (count < 25) return 50
    if (count < 50) return 60
    return 70
  }

  const size = getMarkerSize(facilityCount)

  const icon = new divIcon({
    className: 'custom-marker district-marker-icon',
    html: `<div class="district-marker" style="width: ${size}px; height: ${size}px;">
             <div class="district-marker-text">
               <div style="font-size: ${size > 50 ? '14px' : '12px'};">${facilityCount}</div>
               <div style="font-size: ${size > 50 ? '10px' : '8px'};">${district.name}</div>
             </div>
           </div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    popupAnchor: [0, -(size / 2)],
  })

  // Use district center coordinates (you'd need to add these to your location data)
  // For now, we'll use the centroid of facilities in the district
  const lat = district.center?.latitude || district.latitude || 0
  const lng = district.center?.longitude || district.longitude || 0

  return (
    <Marker position={[lat, lng]} icon={icon}>
      <Popup>
        <div className="min-w-48">
          <h3 className="font-medium text-gray-900 mb-2 capitalize">
            {district.name} District
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-700">
                Total Facilities:
              </span>
              <span className="text-gray-600">{facilityCount}</span>
            </div>
            {district.subcounties && (
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-700">Subcounties:</span>
                <span className="text-gray-600">
                  {district.subcounties.length}
                </span>
              </div>
            )}
          </div>
          <button
            onClick={() => onDistrictClick?.(district)}
            className="mt-3 w-full bg-primary-500 text-white text-sm py-2 px-3 rounded hover:bg-primary-600 transition-colors"
          >
            View District Details
          </button>
        </div>
      </Popup>
    </Marker>
  )
}

function MapView() {
  const { facilities, total, filtered } = useFilteredFacilities()
  const { mapState, showFacilityDetailModal } = useView()
  const { loading } = useData()
  const { activeCategory } = useFilters()
  const { isViewingAllDistricts, setDistrict } = useLocation()
  const mapRef = useRef()
  const insightsPanelRef = useRef(null)

  // Calculate district aggregations for multi-district mode
  const districtAggregations = useMemo(() => {
    if (!isViewingAllDistricts() || !facilities.length) return []

    const districtGroups = facilities.reduce((acc, facility) => {
      const district = facility.district
      if (!district) return acc

      if (!acc[district]) {
        acc[district] = {
          name: district,
          facilities: [],
          // Calculate centroid from facilities (would be better to have actual district centers)
          center: null,
        }
      }
      acc[district].facilities.push(facility)
      return acc
    }, {})

    // Calculate centroids for each district
    return Object.values(districtGroups)
      .map(district => {
        const validFacilities = district.facilities.filter(
          f =>
            f.latitude &&
            f.longitude &&
            !isNaN(f.latitude) &&
            !isNaN(f.longitude)
        )

        if (validFacilities.length > 0) {
          const avgLat =
            validFacilities.reduce(
              (sum, f) => sum + parseFloat(f.latitude),
              0
            ) / validFacilities.length
          const avgLng =
            validFacilities.reduce(
              (sum, f) => sum + parseFloat(f.longitude),
              0
            ) / validFacilities.length
          district.center = { latitude: avgLat, longitude: avgLng }
        }

        return {
          ...district,
          facilityCount: district.facilities.length,
        }
      })
      .filter(district => district.center) // Only include districts with valid coordinates
  }, [facilities, isViewingAllDistricts])

  // Auto-fit bounds when facilities or view mode changes
  useEffect(() => {
    if (!mapRef.current) return

    const map = mapRef.current

    if (isViewingAllDistricts() && districtAggregations.length > 0) {
      // Fit bounds to district centers
      const bounds = districtAggregations.map(d => [
        d.center.latitude,
        d.center.longitude,
      ])
      map.fitBounds(bounds, { padding: [20, 20] })
    } else if (facilities.length > 0) {
      // Standard facility-based bounds
      const validFacilities = facilities.filter(
        f =>
          f.latitude && f.longitude && !isNaN(f.latitude) && !isNaN(f.longitude)
      )

      if (validFacilities.length === 0) return

      if (validFacilities.length === 1) {
        // Single facility - center and zoom
        const facility = validFacilities[0]
        map.setView([facility.latitude, facility.longitude], 14)
      } else {
        // Multiple facilities - fit bounds
        const bounds = validFacilities.map(f => [f.latitude, f.longitude])
        map.fitBounds(bounds, { padding: [20, 20] })
      }
    }
  }, [facilities, districtAggregations, isViewingAllDistricts])

  const handleFacilityClick = facility => {
    showFacilityDetailModal(facility.id)
  }

  const handleDistrictClick = district => {
    // Switch to selected district mode
    // Convert district name to new ID format
    const districtId = `d-${district.name.toLowerCase().replace(/\s+/g, '-')}`
    setDistrict(districtId)
  }

  const scrollToInsights = () => {
    insightsPanelRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    })
  }

  const validFacilities = facilities.filter(
    f => f.latitude && f.longitude && !isNaN(f.latitude) && !isNaN(f.longitude)
  )

  const shouldShowDistrictView =
    isViewingAllDistricts() && districtAggregations.length > 0
  const shouldShowFacilityView =
    !shouldShowDistrictView && validFacilities.length > 0

  // Show loading if facilities are currently being loaded
  if (loading[`facilities_${activeCategory}`]) {
    return (
      <div className="h-96 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="lg" className="mx-auto mb-4" />
          <p className="text-gray-600">Loading map...</p>
        </div>
      </div>
    )
  }

  if (!shouldShowDistrictView && !shouldShowFacilityView) {
    return (
      <div className="h-96 bg-gray-100 rounded-lg flex items-center justify-center">
        <div className="text-center text-gray-500">
          <div className="text-4xl mb-2">🗺️</div>
          <p>
            {isViewingAllDistricts()
              ? 'No district data available to display'
              : 'No facilities with valid coordinates to display'}
          </p>
          {filtered !== total && (
            <p className="text-sm mt-1">Try adjusting your filters</p>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Map Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <span className="text-sm text-gray-600">
            {shouldShowDistrictView
              ? `Showing ${districtAggregations.length} districts on map`
              : `Showing ${validFacilities.length} facilities on map`}
          </span>
          <button
            onClick={scrollToInsights}
            className="flex items-center space-x-2 text-sm text-primary-600 hover:text-primary-700
                       bg-primary-50 hover:bg-primary-100 px-3 py-1.5 rounded-lg transition-all
                       hover:shadow-sm"
            aria-label="Scroll to AI insights"
          >
            <span>✨</span>
            <span>View AI Insights</span>
          </button>
        </div>

        {/* Legend */}
        {shouldShowDistrictView ? (
          <div className="flex items-center space-x-4 text-xs">
            <div className="flex items-center">
              <div className="w-4 h-4 bg-gray-800 rounded-full border-2 border-white mr-2"></div>
              <span>District (click to zoom)</span>
            </div>
          </div>
        ) : (
          <div className="flex items-center space-x-4 text-xs">
            {activeCategory === 'education' ? (
              // Education legend
              <>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded mr-1"></div>
                  <span>🏫 Primary Schools</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
                  <span>🎓 Secondary Schools</span>
                </div>
              </>
            ) : (
              // Health legend
              <>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-green-500 rounded mr-1"></div>
                  <span>HC II</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-blue-500 rounded mr-1"></div>
                  <span>HC III</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-orange-500 rounded mr-1"></div>
                  <span>HC IV</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-red-500 rounded mr-1"></div>
                  <span>Hospital</span>
                </div>
                <div className="flex items-center">
                  <div className="w-3 h-3 bg-purple-500 rounded mr-1"></div>
                  <span>Clinic</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="h-96 rounded-lg overflow-hidden shadow-sm">
        <MapContainer
          center={mapState.center}
          zoom={mapState.zoom}
          className="h-full w-full"
          ref={mapRef}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          />

          {shouldShowDistrictView ? (
            // District markers for "All Districts" view
            <>
              {districtAggregations.map(district => (
                <DistrictMarker
                  key={district.name}
                  district={district}
                  facilityCount={district.facilityCount}
                  onDistrictClick={handleDistrictClick}
                />
              ))}
            </>
          ) : (
            // Facility markers for specific district/location view
            <MarkerClusterGroup
              chunkedLoading
              maxClusterRadius={50}
              disableClusteringAtZoom={15}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              zoomToBoundsOnClick={true}
              iconCreateFunction={cluster => {
                const count = cluster.getChildCount()
                return divIcon({
                  html: `<div class="cluster-marker">
                           <span class="cluster-count">${count}</span>
                         </div>`,
                  className: 'custom-cluster-icon',
                  iconSize: [35, 35],
                  iconAnchor: [17, 17],
                })
              }}
            >
              {validFacilities.map(facility => (
                <FacilityMarker
                  key={facility.id}
                  facility={facility}
                  onViewDetails={handleFacilityClick}
                  category={activeCategory}
                />
              ))}
            </MarkerClusterGroup>
          )}
        </MapContainer>
      </div>

      {/* Map Summary */}
      <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {shouldShowDistrictView ? (
            <>
              <div>
                <span className="font-medium">Total Districts:</span>{' '}
                {districtAggregations.length}
              </div>
              <div>
                <span className="font-medium">Total Facilities:</span> {total}
              </div>
              <div>
                <span className="font-medium">View Mode:</span> All Districts
              </div>
              <div>
                <span className="font-medium">Click districts to zoom</span>
              </div>
            </>
          ) : (
            <>
              <div>
                <span className="font-medium">Total Facilities:</span> {total}
              </div>
              <div>
                <span className="font-medium">Filtered:</span> {filtered}
              </div>
              <div>
                <span className="font-medium">On Map:</span>{' '}
                {validFacilities.length}
              </div>
              <div>
                <span className="font-medium">Missing Coords:</span>{' '}
                {filtered - validFacilities.length}
              </div>
            </>
          )}
        </div>
      </div>

      {/* AI Insights Panel */}
      <div
        ref={insightsPanelRef}
        className="mt-6 bg-white transform transition-all duration-500 ease-out animate-slide-up"
      >
        <InsightsPanel category={activeCategory} />
      </div>
    </div>
  )
}

DistrictMarker.propTypes = {
  district: PropTypes.shape({
    name: PropTypes.string.isRequired,
    center: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    subcounties: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  facilityCount: PropTypes.number.isRequired,
  onDistrictClick: PropTypes.func.isRequired,
}

export default MapView
